import ApiClient from '@/services/apiClient/ApiClient'
import type { GroupLandFavoriteResponse } from '@popety-io/types'

const apiClient = new ApiClient()

export const groupLandFavoritesService = {
  /**
   * Get all group land favorites for the current user
   */
  getAllGroupLandFavorites: async (): Promise<GroupLandFavoriteResponse[]> => {
    try {
      const response = await apiClient.get('/favorites/group-land')
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandFavoriteResponse[]
    } catch (error: any) {
      console.error('Failed to get all group land favorites:', error)
      throw new Error(error.message || 'Failed to fetch group land favorites')
    }
  },

  /**
   * Get favorites for a specific group land
   */
  getFavoritesByGroupLandId: async (
    groupLandId: string
  ): Promise<GroupLandFavoriteResponse[]> => {
    try {
      const response = await apiClient.get(
        `/favorites/group-land/by-group-land/${groupLandId}`
      )
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandFavoriteResponse[]
    } catch (error: any) {
      console.error(
        `Failed to get favorites for group land ${groupLandId}:`,
        error
      )
      throw new Error(
        error.message || 'Failed to fetch favorites for group land'
      )
    }
  },

  /**
   * Get a specific group land favorite by ID
   */
  getGroupLandFavoriteById: async (
    favoriteId: string
  ): Promise<GroupLandFavoriteResponse> => {
    try {
      const response = await apiClient.get(
        `/favorites/group-land/${favoriteId}`
      )
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandFavoriteResponse
    } catch (error: any) {
      console.error(`Failed to get group land favorite ${favoriteId}:`, error)
      throw new Error(error.message || 'Failed to fetch group land favorite')
    }
  },

  /**
   * Create a new favorite for a group land
   */
  createGroupLandFavorite: async (
    groupLandId: string,
    shared: boolean = false
  ): Promise<GroupLandFavoriteResponse> => {
    try {
      const response = await apiClient.post('/favorites/group-land', {
        groupLandId,
        shared,
      })
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
      return response.data as GroupLandFavoriteResponse
    } catch (error: any) {
      console.error(
        `Failed to create favorite for group land ${groupLandId}:`,
        error
      )
      throw new Error(error.message || 'Failed to create group land favorite')
    }
  },

  /**
   * Delete a group land favorite
   */
  deleteGroupLandFavorite: async (favoriteId: string): Promise<void> => {
    try {
      const response = await apiClient.delete(
        `/favorites/group-land/${favoriteId}`
      )
      if (response.errors) {
        throw new Error(response.errors[0].message)
      }
    } catch (error: any) {
      console.error(
        `Failed to delete group land favorite ${favoriteId}:`,
        error
      )
      throw new Error(error.message || 'Failed to delete group land favorite')
    }
  },

  /**
   * Toggle favorite status for a group land (helper function)
   * Checks if favorite exists and creates/deletes accordingly
   */
  toggleGroupLandFavorite: async (
    groupLandId: string,
    shared: boolean = false
  ): Promise<{ isFavorite: boolean; favorite?: GroupLandFavoriteResponse }> => {
    try {
      // Get existing favorites for this group land
      const favorites =
        await groupLandFavoritesService.getFavoritesByGroupLandId(groupLandId)

      // Check if already favorited by current user
      const existingFavorite = favorites.find((fav) => fav.active)

      if (existingFavorite) {
        // Remove favorite
        await groupLandFavoritesService.deleteGroupLandFavorite(
          existingFavorite.id
        )
        return { isFavorite: false }
      } else {
        // Add favorite
        const newFavorite =
          await groupLandFavoritesService.createGroupLandFavorite(
            groupLandId,
            shared
          )
        return { isFavorite: true, favorite: newFavorite }
      }
    } catch (error: any) {
      console.error(
        `Failed to toggle favorite for group land ${groupLandId}:`,
        error
      )
      throw new Error(error.message || 'Failed to toggle group land favorite')
    }
  },
}
